/**
 * Federal + NY State + NYC tax brackets for tax year 2025.
 *
 * **Sources** (all retrieved 2026-05-08):
 *
 * - **Federal ordinary income brackets + standard deduction + AMT
 *   exemption + LTCG/QDIV brackets**: IRS Rev Proc 2024-40 (released
 *   October 2024). https://www.irs.gov/pub/irs-drop/rp-24-40.pdf
 * - **NY State ordinary income brackets + standard deduction**: NY Tax
 *   Law § 601 + IT-201 instructions (Form IT-201-I, 2025).
 * - **NYC resident income tax brackets**: NYC Admin Code § 11-1701 +
 *   Form NYC-1127 instructions (2025).
 *
 * **Modeling notes:**
 *
 * - All amounts integer cents per the Hearth money-as-cents rule.
 * - Brackets are **half-open ranges** [min_cents, max_cents). The top
 *   bracket has `max_cents: null`.
 * - **MFS** (married filing separately) federal brackets are exactly
 *   half of MFJ for the first 5 brackets, then rate-aligned but with
 *   different breakpoints — modeled explicitly here, not derived from
 *   MFJ to avoid silent drift.
 * - **HoH** (head of household) gets its own bracket schedule per IRC
 *   § 1(b); not derivable from single/MFJ.
 * - **QSS** (qualifying surviving spouse) uses MFJ brackets per IRC §
 *   1(a) for 2 years after spouse's death.
 * - **NY** has a **9-bracket schedule** (top three brackets at
 *   $1.077M / $5M / $25M kick in only for very high earners). Modeled
 *   completely.
 * - **NYC** has a **4-bracket schedule** for residents; non-residents
 *   pay $0 NYC tax.
 *
 * **Self-contained types** — this data file defines its own TaxBracket
 * + FilingStatus types so the file mirrors verbatim from
 * `shared/src/data/` to `web/src/lib/data/` (no cross-package imports).
 * The canonical FilingStatus type is re-exported from `state-tax.ts`
 * (the original Phase 5.5 home) and widened from 2 values to 5 in
 * Phase 7 Session 1.
 */

export type FilingStatus = "single" | "mfj" | "mfs" | "hoh" | "qss";

export interface TaxBracket {
  /** Lower bound, inclusive, integer cents. */
  readonly min_cents: number;
  /** Upper bound, exclusive, integer cents. `null` = open-ended top
   *  bracket. */
  readonly max_cents: number | null;
  /** Marginal rate as a percentage (e.g., 22 for 22%). */
  readonly rate_pct: number;
}

export interface FederalBracketTable {
  readonly tax_year: number;
  readonly source: string;
  readonly retrieved_at: string;
  readonly ordinary: Record<FilingStatus, readonly TaxBracket[]>;
  /** LTCG + qualified dividends rate schedule per IRC § 1(h). 0% / 15%
   *  / 20%. Stacked ABOVE ordinary income (taxed at the rate dictated
   *  by total taxable income, ordinary first). */
  readonly ltcg: Record<FilingStatus, readonly TaxBracket[]>;
  /** Standard deduction by filing status. */
  readonly standard_deduction_cents: Record<FilingStatus, number>;
  /** AMT exemption amount (subtracted from AMT income). */
  readonly amt_exemption_cents: Record<FilingStatus, number>;
  /** AMT exemption phase-out begins above this AMT income. Phases out
   *  $0.25 per $1 over the threshold until exemption = 0. */
  readonly amt_phase_out_threshold_cents: Record<FilingStatus, number>;
  /** AMT 26% rate applies up to this AMT taxable income; 28% above.
   *  Same threshold for all statuses except MFS which is half. */
  readonly amt_rate_threshold_cents: Record<FilingStatus, number>;
}

export interface StateBracketTable {
  readonly tax_year: number;
  readonly state: string;
  readonly source: string;
  readonly retrieved_at: string;
  readonly ordinary: Record<FilingStatus, readonly TaxBracket[]>;
  /** Standard deduction by filing status. NY has explicit deductions;
   *  NYC piggybacks on NY taxable income — NYC uses 0 for all. */
  readonly standard_deduction_cents: Record<FilingStatus, number>;
}

// === Federal 2025 (Rev Proc 2024-40) =======================================

export const FEDERAL_2025: FederalBracketTable = {
  tax_year: 2025,
  source: "IRS Rev Proc 2024-40",
  retrieved_at: "2026-05-08",
  ordinary: {
    single: [
      { min_cents: 0, max_cents: 11_925_00, rate_pct: 10 },
      { min_cents: 11_925_00, max_cents: 48_475_00, rate_pct: 12 },
      { min_cents: 48_475_00, max_cents: 103_350_00, rate_pct: 22 },
      { min_cents: 103_350_00, max_cents: 197_300_00, rate_pct: 24 },
      { min_cents: 197_300_00, max_cents: 250_525_00, rate_pct: 32 },
      { min_cents: 250_525_00, max_cents: 626_350_00, rate_pct: 35 },
      { min_cents: 626_350_00, max_cents: null, rate_pct: 37 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 23_850_00, rate_pct: 10 },
      { min_cents: 23_850_00, max_cents: 96_950_00, rate_pct: 12 },
      { min_cents: 96_950_00, max_cents: 206_700_00, rate_pct: 22 },
      { min_cents: 206_700_00, max_cents: 394_600_00, rate_pct: 24 },
      { min_cents: 394_600_00, max_cents: 501_050_00, rate_pct: 32 },
      { min_cents: 501_050_00, max_cents: 751_600_00, rate_pct: 35 },
      { min_cents: 751_600_00, max_cents: null, rate_pct: 37 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 11_925_00, rate_pct: 10 },
      { min_cents: 11_925_00, max_cents: 48_475_00, rate_pct: 12 },
      { min_cents: 48_475_00, max_cents: 103_350_00, rate_pct: 22 },
      { min_cents: 103_350_00, max_cents: 197_300_00, rate_pct: 24 },
      { min_cents: 197_300_00, max_cents: 250_525_00, rate_pct: 32 },
      { min_cents: 250_525_00, max_cents: 375_800_00, rate_pct: 35 },
      { min_cents: 375_800_00, max_cents: null, rate_pct: 37 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 17_000_00, rate_pct: 10 },
      { min_cents: 17_000_00, max_cents: 64_850_00, rate_pct: 12 },
      { min_cents: 64_850_00, max_cents: 103_350_00, rate_pct: 22 },
      { min_cents: 103_350_00, max_cents: 197_300_00, rate_pct: 24 },
      { min_cents: 197_300_00, max_cents: 250_500_00, rate_pct: 32 },
      { min_cents: 250_500_00, max_cents: 626_350_00, rate_pct: 35 },
      { min_cents: 626_350_00, max_cents: null, rate_pct: 37 },
    ],
    // QSS uses MFJ schedule per IRC § 1(a).
    qss: [
      { min_cents: 0, max_cents: 23_850_00, rate_pct: 10 },
      { min_cents: 23_850_00, max_cents: 96_950_00, rate_pct: 12 },
      { min_cents: 96_950_00, max_cents: 206_700_00, rate_pct: 22 },
      { min_cents: 206_700_00, max_cents: 394_600_00, rate_pct: 24 },
      { min_cents: 394_600_00, max_cents: 501_050_00, rate_pct: 32 },
      { min_cents: 501_050_00, max_cents: 751_600_00, rate_pct: 35 },
      { min_cents: 751_600_00, max_cents: null, rate_pct: 37 },
    ],
  },
  ltcg: {
    single: [
      { min_cents: 0, max_cents: 48_350_00, rate_pct: 0 },
      { min_cents: 48_350_00, max_cents: 533_400_00, rate_pct: 15 },
      { min_cents: 533_400_00, max_cents: null, rate_pct: 20 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 96_700_00, rate_pct: 0 },
      { min_cents: 96_700_00, max_cents: 600_050_00, rate_pct: 15 },
      { min_cents: 600_050_00, max_cents: null, rate_pct: 20 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 48_350_00, rate_pct: 0 },
      { min_cents: 48_350_00, max_cents: 300_000_00, rate_pct: 15 },
      { min_cents: 300_000_00, max_cents: null, rate_pct: 20 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 64_750_00, rate_pct: 0 },
      { min_cents: 64_750_00, max_cents: 566_700_00, rate_pct: 15 },
      { min_cents: 566_700_00, max_cents: null, rate_pct: 20 },
    ],
    qss: [
      { min_cents: 0, max_cents: 96_700_00, rate_pct: 0 },
      { min_cents: 96_700_00, max_cents: 600_050_00, rate_pct: 15 },
      { min_cents: 600_050_00, max_cents: null, rate_pct: 20 },
    ],
  },
  standard_deduction_cents: {
    single: 15_000_00,
    mfj: 30_000_00,
    mfs: 15_000_00,
    hoh: 22_500_00,
    qss: 30_000_00,
  },
  amt_exemption_cents: {
    single: 88_100_00,
    mfj: 137_000_00,
    mfs: 68_500_00,
    hoh: 88_100_00,
    qss: 137_000_00,
  },
  amt_phase_out_threshold_cents: {
    single: 626_350_00,
    mfj: 1_252_700_00,
    mfs: 626_350_00,
    hoh: 626_350_00,
    qss: 1_252_700_00,
  },
  // 26% / 28% AMT rate breakpoint. MFS uses half the all-others
  // threshold per IRC § 55(b)(1)(A).
  amt_rate_threshold_cents: {
    single: 232_600_00,
    mfj: 232_600_00,
    mfs: 116_300_00,
    hoh: 232_600_00,
    qss: 232_600_00,
  },
};

// === New York State 2025 (NY Tax Law § 601) ================================

export const NY_2025: StateBracketTable = {
  tax_year: 2025,
  state: "NY",
  source: "NY Tax Law § 601 + IT-201-I (2025)",
  retrieved_at: "2026-05-08",
  ordinary: {
    single: [
      { min_cents: 0, max_cents: 8_500_00, rate_pct: 4.0 },
      { min_cents: 8_500_00, max_cents: 11_700_00, rate_pct: 4.5 },
      { min_cents: 11_700_00, max_cents: 13_900_00, rate_pct: 5.25 },
      { min_cents: 13_900_00, max_cents: 80_650_00, rate_pct: 5.5 },
      { min_cents: 80_650_00, max_cents: 215_400_00, rate_pct: 6.0 },
      { min_cents: 215_400_00, max_cents: 1_077_550_00, rate_pct: 6.85 },
      { min_cents: 1_077_550_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 17_150_00, rate_pct: 4.0 },
      { min_cents: 17_150_00, max_cents: 23_600_00, rate_pct: 4.5 },
      { min_cents: 23_600_00, max_cents: 27_900_00, rate_pct: 5.25 },
      { min_cents: 27_900_00, max_cents: 161_550_00, rate_pct: 5.5 },
      { min_cents: 161_550_00, max_cents: 323_200_00, rate_pct: 6.0 },
      { min_cents: 323_200_00, max_cents: 2_155_350_00, rate_pct: 6.85 },
      { min_cents: 2_155_350_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 8_500_00, rate_pct: 4.0 },
      { min_cents: 8_500_00, max_cents: 11_700_00, rate_pct: 4.5 },
      { min_cents: 11_700_00, max_cents: 13_900_00, rate_pct: 5.25 },
      { min_cents: 13_900_00, max_cents: 80_650_00, rate_pct: 5.5 },
      { min_cents: 80_650_00, max_cents: 215_400_00, rate_pct: 6.0 },
      { min_cents: 215_400_00, max_cents: 1_077_550_00, rate_pct: 6.85 },
      { min_cents: 1_077_550_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 12_800_00, rate_pct: 4.0 },
      { min_cents: 12_800_00, max_cents: 17_650_00, rate_pct: 4.5 },
      { min_cents: 17_650_00, max_cents: 20_900_00, rate_pct: 5.25 },
      { min_cents: 20_900_00, max_cents: 107_650_00, rate_pct: 5.5 },
      { min_cents: 107_650_00, max_cents: 269_300_00, rate_pct: 6.0 },
      { min_cents: 269_300_00, max_cents: 1_616_450_00, rate_pct: 6.85 },
      { min_cents: 1_616_450_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
    qss: [
      { min_cents: 0, max_cents: 17_150_00, rate_pct: 4.0 },
      { min_cents: 17_150_00, max_cents: 23_600_00, rate_pct: 4.5 },
      { min_cents: 23_600_00, max_cents: 27_900_00, rate_pct: 5.25 },
      { min_cents: 27_900_00, max_cents: 161_550_00, rate_pct: 5.5 },
      { min_cents: 161_550_00, max_cents: 323_200_00, rate_pct: 6.0 },
      { min_cents: 323_200_00, max_cents: 2_155_350_00, rate_pct: 6.85 },
      { min_cents: 2_155_350_00, max_cents: 5_000_000_00, rate_pct: 9.65 },
      { min_cents: 5_000_000_00, max_cents: 25_000_000_00, rate_pct: 10.3 },
      { min_cents: 25_000_000_00, max_cents: null, rate_pct: 10.9 },
    ],
  },
  // NY standard deductions per Form IT-201-I 2025 instructions, page
  // 23 ("Standard deduction table").
  standard_deduction_cents: {
    single: 8_000_00,
    mfj: 16_050_00,
    mfs: 8_000_00,
    hoh: 11_200_00,
    qss: 16_050_00,
  },
};

// === New York City 2025 (NYC Admin Code § 11-1701) =========================

export const NYC_2025: StateBracketTable = {
  tax_year: 2025,
  state: "NYC",
  source: "NYC Admin Code § 11-1701",
  retrieved_at: "2026-05-08",
  ordinary: {
    single: [
      { min_cents: 0, max_cents: 12_000_00, rate_pct: 3.078 },
      { min_cents: 12_000_00, max_cents: 25_000_00, rate_pct: 3.762 },
      { min_cents: 25_000_00, max_cents: 50_000_00, rate_pct: 3.819 },
      { min_cents: 50_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    mfj: [
      { min_cents: 0, max_cents: 21_600_00, rate_pct: 3.078 },
      { min_cents: 21_600_00, max_cents: 45_000_00, rate_pct: 3.762 },
      { min_cents: 45_000_00, max_cents: 90_000_00, rate_pct: 3.819 },
      { min_cents: 90_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    mfs: [
      { min_cents: 0, max_cents: 12_000_00, rate_pct: 3.078 },
      { min_cents: 12_000_00, max_cents: 25_000_00, rate_pct: 3.762 },
      { min_cents: 25_000_00, max_cents: 50_000_00, rate_pct: 3.819 },
      { min_cents: 50_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    hoh: [
      { min_cents: 0, max_cents: 14_400_00, rate_pct: 3.078 },
      { min_cents: 14_400_00, max_cents: 30_000_00, rate_pct: 3.762 },
      { min_cents: 30_000_00, max_cents: 60_000_00, rate_pct: 3.819 },
      { min_cents: 60_000_00, max_cents: null, rate_pct: 3.876 },
    ],
    qss: [
      { min_cents: 0, max_cents: 21_600_00, rate_pct: 3.078 },
      { min_cents: 21_600_00, max_cents: 45_000_00, rate_pct: 3.762 },
      { min_cents: 45_000_00, max_cents: 90_000_00, rate_pct: 3.819 },
      { min_cents: 90_000_00, max_cents: null, rate_pct: 3.876 },
    ],
  },
  // NYC piggybacks on NY taxable income — no NYC-specific standard
  // deduction. Set to zero so callers don't double-count NY's deduction.
  standard_deduction_cents: {
    single: 0,
    mfj: 0,
    mfs: 0,
    hoh: 0,
    qss: 0,
  },
};
